import React, { memo } from 'react';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { LOGIN_ACCENT } from '../auth/loginDarkTheme';
import { THEME_COLORS } from '../../shared/constants/theme';
import { getGridColumnLineColor } from '../../utils/dashboardGridCalendarLayout';

/**
 * DateHeader Component
 * 3D Material Design date header with compact height
 */
const DateHeader = ({
  dateInfo,
  isToday,
  isDarkMode,
  isLast,
  variant = 'default',
  density = 'default',
  isWeekend = false,
  showMonthLabel = false,
  columnWidth,
}) => {
  const isCompact = variant === 'compact';
  const isWorkspace = variant === 'workspace';
  const isDense = density === 'compact';

  if (isCompact) {
    return (
      <Box
        sx={{
          minWidth: 0,
          width: '100%',
          py: 0.35,
          px: 0.15,
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 0.1,
          position: 'relative',
          background: isToday
            ? `linear-gradient(160deg, ${LOGIN_ACCENT} 0%, ${alpha(LOGIN_ACCENT, 0.72)} 100%)`
            : isWeekend
              ? 'rgba(255,255,255,0.025)'
              : 'transparent',
          borderRight: !isLast ? '1px solid rgba(255,255,255,0.06)' : 'none',
          borderRadius: isToday ? '8px 8px 0 0' : 0,
          boxShadow: isToday ? `0 2px 10px ${alpha(LOGIN_ACCENT, 0.35)}` : 'none',
        }}
      >
        <Box
          component="span"
          sx={{
            fontSize: '0.52rem',
            fontWeight: 700,
            color: isToday ? '#04120e' : 'rgba(148,163,184,0.95)',
            textTransform: 'uppercase',
            lineHeight: 1,
            letterSpacing: '0.04em',
          }}
        >
          {dateInfo.day.charAt(0)}
        </Box>
        <Box
          component="span"
          sx={{
            fontSize: '0.72rem',
            fontWeight: 800,
            color: isToday ? '#04120e' : '#f8fafc',
            lineHeight: 1,
            WebkitTextFillColor: isToday ? '#04120e' : '#f8fafc',
          }}
        >
          {parseInt(dateInfo.dayNum, 10)}
        </Box>
        {showMonthLabel ? (
          <Box
            component="span"
            sx={{
              fontSize: '0.48rem',
              fontWeight: 600,
              color: isToday ? alpha('#04120e', 0.75) : 'rgba(148,163,184,0.85)',
              lineHeight: 1,
            }}
          >
            {dateInfo.month}
          </Box>
        ) : null}
      </Box>
    );
  }

  if (isWorkspace) {
    const todayText = isDarkMode ? LOGIN_ACCENT : '#0f766e';
    const mutedText = isDarkMode ? alpha('#cbd5e1', 0.72) : '#64748b';
    const headerBg = isDarkMode ? THEME_COLORS.SURFACE_HEADER_DARK : '#f8fafc';
    const weekendBg = isDarkMode ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#f1f5f9';

    return (
      <Box
        sx={{
          minWidth: 0,
          width: '100%',
          py: isDense ? 0.55 : 0.85,
          px: isDense ? 0.25 : 0.5,
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: isDense ? 0.05 : 0.15,
          position: 'relative',
          background: isToday
            ? headerBg
            : isWeekend
              ? weekendBg
              : (isDarkMode ? THEME_COLORS.BACKGROUND_DARK : '#f8fafc'),
          borderRight: !isLast
            ? `1px solid ${getGridColumnLineColor(isDarkMode ? 'dark' : 'light')}`
            : 'none',
          ...(isToday
            ? {
                boxShadow: `inset 0 2px 0 0 ${alpha(LOGIN_ACCENT, 0.35)}`,
              }
            : {}),
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 800,
            fontSize: isDense ? '0.5rem' : '0.58rem',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: isToday ? todayText : mutedText,
            lineHeight: 1,
          }}
        >
          {isDense ? dateInfo.day.charAt(0) : dateInfo.day}
        </Typography>
        <Typography
          component="div"
          sx={{
            fontWeight: 800,
            fontSize: isDense ? '0.82rem' : '1.05rem',
            lineHeight: 1,
            color: isToday ? todayText : isDarkMode ? '#e2e8f0' : '#0f172a',
          }}
        >
          {dateInfo.dayNum}
        </Typography>
        {!isDense && (
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              fontSize: '0.58rem',
              color: isToday ? alpha(todayText, 0.82) : isDarkMode ? alpha('#94a3b8', 0.9) : '#64748b',
              lineHeight: 1.1,
            }}
          >
            {dateInfo.month} {dateInfo.year}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: '100px',
        p: 0.75,
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
        background: isToday
          ? 'linear-gradient(135deg, #4ce1b6 0%, #3ab896 100%)'
          : (isDarkMode 
              ? 'linear-gradient(135deg, #2a2a31 0%, #1e1e24 100%)'
              : 'linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%)'),
        borderRight: !isLast
          ? `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`
          : 'none',
        // 3D Box Shadow Effect
        boxShadow: isToday
          ? 'inset 0 2px 4px rgba(255, 255, 255, 0.3), 0 2px 8px rgba(76, 225, 182, 0.4), 0 1px 3px rgba(0, 0, 0, 0.2)'
          : (isDarkMode
              ? 'inset 0 1px 2px rgba(255, 255, 255, 0.05), 0 2px 6px rgba(0, 0, 0, 0.4), 0 1px 2px rgba(0, 0, 0, 0.3)'
              : 'inset 0 1px 2px rgba(255, 255, 255, 0.8), 0 2px 6px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.05)'),
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: 'translateZ(0)',
        '&:hover': {
          transform: 'translateY(-1px) translateZ(0)',
          boxShadow: isToday
            ? 'inset 0 2px 4px rgba(255, 255, 255, 0.3), 0 4px 12px rgba(76, 225, 182, 0.5), 0 2px 4px rgba(0, 0, 0, 0.2)'
            : (isDarkMode
                ? 'inset 0 1px 2px rgba(255, 255, 255, 0.05), 0 4px 10px rgba(0, 0, 0, 0.5), 0 2px 4px rgba(0, 0, 0, 0.3)'
                : 'inset 0 1px 2px rgba(255, 255, 255, 0.8), 0 4px 10px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.08)'),
        },
        // Top highlight for 3D effect
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '2px',
          background: isToday
            ? 'linear-gradient(90deg, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0.2) 50%, rgba(255, 255, 255, 0.4) 100%)'
            : (isDarkMode
                ? 'linear-gradient(90deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 50%, rgba(255, 255, 255, 0.1) 100%)'
                : 'linear-gradient(90deg, rgba(255, 255, 255, 0.8) 0%, rgba(255, 255, 255, 0.4) 50%, rgba(255, 255, 255, 0.8) 100%)'),
          borderRadius: '4px 4px 0 0',
        },
        // Bottom shadow for depth
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: isDarkMode
            ? 'linear-gradient(90deg, transparent 0%, rgba(0, 0, 0, 0.3) 50%, transparent 100%)'
            : 'linear-gradient(90deg, transparent 0%, rgba(0, 0, 0, 0.1) 50%, transparent 100%)',
        },
      }}
    >
      {/* Day of week */}
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          fontWeight: 700,
          color: isToday ? '#000' : (isDarkMode ? '#b0b0b0' : '#666'),
          fontSize: '0.65rem',
          textTransform: 'uppercase',
          lineHeight: 1.2,
          letterSpacing: '1px',
          mb: 0.15,
          textShadow: isToday 
            ? '0 1px 2px rgba(0, 0, 0, 0.1)'
            : (isDarkMode ? '0 1px 2px rgba(0, 0, 0, 0.5)' : 'none'),
        }}
      >
        {dateInfo.day}
      </Typography>
      
      {/* Date number - prominent with 3D text effect */}
      <Typography
        variant="h6"
        component="div"
        sx={{
          fontWeight: 800,
          color: isToday ? '#000' : (isDarkMode ? '#fff' : '#212121'),
          fontSize: '1rem',
          lineHeight: 1,
          mb: 0.15,
          textShadow: isToday
            ? '0 2px 4px rgba(0, 0, 0, 0.2), 0 1px 2px rgba(255, 255, 255, 0.5)'
            : (isDarkMode
                ? '0 2px 4px rgba(0, 0, 0, 0.8), 0 1px 2px rgba(255, 255, 255, 0.1)'
                : '0 1px 2px rgba(0, 0, 0, 0.1), 0 1px 1px rgba(255, 255, 255, 0.8)'),
        }}
      >
        {dateInfo.dayNum}
      </Typography>
      
      {/* Month and year */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, flexWrap: 'wrap', justifyContent: 'center' }}>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            color: isToday ? '#000' : (isDarkMode ? '#999' : '#757575'),
            fontSize: '0.65rem',
            lineHeight: 1.1,
          }}
        >
          {dateInfo.month}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 500,
            color: isToday ? '#000' : (isDarkMode ? '#777' : '#999'),
            fontSize: '0.6rem',
            lineHeight: 1.1,
          }}
        >
          {dateInfo.year}
        </Typography>
      </Box>
    </Box>
  );
};

export default memo(DateHeader);
